/**
 * useCallLinks — every live booking link in the app, as the ONE call picker
 * reads them (2026-09-24).
 *
 * The call pickers used to list call TYPES ("Documentation Call") and let the
 * server decide whose calendar it lands on (playbook → client role → sender).
 * Michael: "Megan needed to book a documentation call for herself for one of
 * my clients on a project but it defaulted my link." So the picker now lists
 * LINKS — a person × a call — grouped by person, the viewer's own first, with
 * a type-ahead that matches a call or a person. Picking one binds that exact
 * link to the to-do (`event_type_id`), and both "book it now" and "send via
 * email instead" use it.
 *
 * One request per page load: the list is shared across every picker on the
 * screen and refreshed at most once a minute.
 */
import { useEffect, useState } from 'react';
import { projectBookingApi, type CallLink } from '../../services/projectBookingApi';
import type { TodoMenuOption } from './TodoMenu';

export type { CallLink };

const FLOOR_LABEL: Record<string, string> = {
  deliver_by: 'deliver-by date',
  doc_call_at: 'documentation call date',
  delivery_call_at: 'delivery call date',
  training_call_at: 'training call date',
};

let cache: { at: number; promise: Promise<CallLink[]> } | null = null;

/** The shared, briefly-cached read. A failure is not cached. */
export function loadCallLinks(): Promise<CallLink[]> {
  if (cache && Date.now() - cache.at < 60_000) return cache.promise;
  const promise = projectBookingApi.callLinks().then((rows) => rows || []);
  cache = { at: Date.now(), promise };
  promise.catch(() => { cache = null; });
  return promise;
}

export function useCallLinks(enabled = true): { links: CallLink[] | null; error: string | null } {
  const [links, setLinks] = useState<CallLink[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    loadCallLinks()
      .then((rows) => { if (alive) { setLinks(rows); setError(null); } })
      .catch(() => { if (alive) { setLinks([]); setError('The booking links didn’t load — close and reopen to retry.'); } });
    return () => { alive = false; };
  }, [enabled]);
  return { links, error };
}

/** The call types a surface offers by name (`scheduling_event_templates.key`). */
export const ASSESSMENT_CALL = 'assessment-call';
export const KICKOFF_CALL = 'kickoff-call';

/** One call type's links, with the people named in `prefer` first (the client's PM, then you). */
export function linksOfCall(links: CallLink[], templateKey: string, prefer: Array<number | null | undefined> = []): CallLink[] {
  const rank = (l: CallLink) => {
    const at = prefer.indexOf(l.host_user_id);
    return at === -1 ? prefer.length : at;
  };
  return links
    .filter((l) => l.template_key === templateKey)
    .sort((a, b) => rank(a) - rank(b) || (a.host_name || '').localeCompare(b.host_name || ''));
}

/** What a new client's first call can be: the assessment when one was sold, and Kickoff
 *  (Michael, 2026-10-30: "selects assessment from drop down (or kickoff if they want to do that first)"). */
export function firstCallLinks(links: CallLink[], assessmentSold: boolean, prefer: Array<number | null | undefined> = []): CallLink[] {
  return [
    ...(assessmentSold ? linksOfCall(links, ASSESSMENT_CALL, prefer) : []),
    ...linksOfCall(links, KICKOFF_CALL, prefer),
  ];
}

/** The project date a link's call type books from, in words ("deliver-by date"). */
export function callLinkFloorLabel(l: Pick<CallLink, 'floor_field'>): string | null {
  return l.floor_field ? FLOOR_LABEL[l.floor_field] ?? null : null;
}

/** "30m · from deliver-by date" — the consequence of the pick, before it is made. */
export function callLinkMeta(l: Pick<CallLink, 'duration_minutes' | 'floor_field'>): string {
  const floor = callLinkFloorLabel(l);
  return floor ? `${l.duration_minutes}m · from ${floor}` : `${l.duration_minutes}m`;
}

/** Whose calendar, in the picker's voice. */
export function callLinkOwner(l: CallLink, meId: number | null | undefined): string {
  return meId != null && l.host_user_id === meId ? 'Your calendar' : `${l.host_name || 'Someone'}’s calendar`;
}

/**
 * The picker rows: grouped by person (yours first, then everyone else by
 * name); inside a person, project calls before personal ones, then by name.
 * Values are `event_type_id`. TodoMenu emits a kicker whenever the group
 * changes, and its type-ahead matches the group, so "megan" finds her calls.
 */
export function callLinkOptions(links: CallLink[], meId: number | null | undefined): TodoMenuOption[] {
  const mine = (l: CallLink) => meId != null && l.host_user_id === meId;
  const sorted = [...links].sort((a, b) => {
    if (mine(a) !== mine(b)) return mine(a) ? -1 : 1;
    const byHost = (a.host_name || '').localeCompare(b.host_name || '');
    if (byHost) return byHost;
    const proj = (l: CallLink) => (l.kind === 'operations' ? 0 : 1);
    if (proj(a) !== proj(b)) return proj(a) - proj(b);
    return a.name.localeCompare(b.name);
  });
  return sorted.map((l) => ({
    value: l.event_type_id,
    label: l.name,
    group: mine(l) ? `Your links${l.host_name ? ' · ' + l.host_name : ''}` : (l.host_name || 'Unnamed teammate'),
    meta: callLinkMeta(l),
  }));
}
